// AutonomousAgent.js
// Advanced autonomous AI agent with reasoning, planning, and browser automation
import React, { useState, useEffect, useRef } from 'react';
import './AutonomousAgent.css';

const OPERATOR_CONFIRM_KEY = 'zolaid-operator-confirm';
const OPERATOR_TAKEOVER_KEY = 'zolaid-operator-takeover';

export default function AutonomousAgent({
  task,
  onComplete,
  onProgress,
  isActive = false,
  browserEnabled = true,
  onBrowserAction = null,
  onConfirmRequired = null,
  onTakeoverRequired = null,
}) {
  const [state, setState] = useState('idle'); // idle, planning, executing, reviewing, complete, error
  const [plan, setPlan] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [logs, setLogs] = useState([]);
  const [thoughts, setThoughts] = useState([]);
  const [browserState, setBrowserState] = useState({ url: '', actions: [] });
  const [retryCount, setRetryCount] = useState(0);
  /** Orchestrator: selected environment (browser | vm | image | video | hybrid) and reason */
  const [orchestrator, setOrchestrator] = useState({ environment: null, capabilityReason: null, suggestVmPermission: false });
  const MAX_RETRIES = 3;
  const abortControllerRef = useRef(null);
  // Past experience: last N task outcomes for adjusting planning heuristics
  const pastExperienceRef = useRef([]);
  const MAX_PAST_EXPERIENCE = 10;

  // Add log entry
  const addLog = (type, message, data = null) => {
    const entry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      type, // info, success, error, thought, action
      message,
      data,
    };
    setLogs(prev => [...prev, entry]);
    if (onProgress) {
      onProgress(entry);
    }
  };

  // Add thought process (reasoning)
  const addThought = (thought) => {
    setThoughts(prev => [...prev, {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      content: thought,
    }]);
    addLog('thought', thought);
  };

  // Build past-experience context for planning heuristics
  const getPastExperienceContext = () => {
    const past = pastExperienceRef.current;
    if (!past.length) return '';
    const lines = past.slice(-5).map(p => `- Task: "${(p.task || '').slice(0, 80)}..." | Success: ${p.success} | Steps: ${p.stepsCount || '?'}${p.notes ? ` | ${p.notes}` : ''}`);
    return `\n\nPAST EXPERIENCE (adjust planning heuristics based on this when relevant):\n${lines.join('\n')}\nUse this to prefer strategies that worked and avoid approaches that failed.`;
  };

  // Analyze task and create execution plan (with past-experienceâ€“aware heuristics)
  const createPlan = async (taskDescription) => {
    try {
      setState('planning');
      addThought('Analyzing task and breaking it down into steps...');
      const pastContext = getPastExperienceContext();
      if (pastContext) addThought('Adjusting planning heuristics based on past experience.');

      // Call AI to generate plan
      const apiBase = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
      const endpoint = apiBase ? `${apiBase}/chat` : '/chat';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: 'autonomous-agent',
          // Our backend expects ONE JSON object only; put the step objects in artifacts.steps.
          message: `Return ONLY ONE JSON object (no markdown). Mode MUST be "plan".

You are the CENTRAL ORCHESTRATOR for an autonomous agent. You sit above all execution environments. Your job is to:
1) Interpret user intent and classify the task type.
2) Select the correct execution environment using the Capability Router (see below).
3) Return a plan with steps ONLY for the chosen environment. Do NOT mix VM and browser in the same plan unless it is an explicit hybrid workflow (browser â†’ VM â†’ browser).
4) Monitor results; retry or escalate if a step fails (e.g. retry same step, or suggest VM if browser cannot do it).
When VM is needed, you may ask the user: "This requires system-level execution. Switch to VM mode?" â€” set suggestVmPermission: true so the UI shows the permission prompt.

CAPABILITY ROUTER (structured task detection â€” use this to choose "environment"):
- URL interaction, web forms, clicking, scraping, using SaaS in a browser â†’ environment: "browser"
- Code execution, file manipulation, installing libraries, system tools, scripts â†’ environment: "vm" (set suggestVmPermission: true)
- Local file transformation â†’ environment: "vm"
- Image generation/editing â†’ environment: "image"
- Video processing â†’ environment: "video"
- Cross-tool workflow (e.g. browser downloads file then process it) â†’ environment: "vm" or "hybrid", suggestVmPermission: true

BROWSER OPERATOR stays LIGHTWEIGHT (do NOT merge browser + VM):
- Web-only: clicking, scraping, filling forms, using SaaS tools. Ephemeral session.
- No heavy installs, no OS-level power. If the task needs code execution, file installs, or system access â†’ choose "vm" and set suggestVmPermission: true; do NOT overload browser with VM responsibilities.

VM is "POWER MODE" (code, files, installs, multi-step automation, cross-domain pipelines). VM requires explicit user permission (limits, timeouts, session isolation); always set suggestVmPermission: true when environment is "vm" or "hybrid".

TASK: ${taskDescription}
${pastContext}

First decide environment, then output steps only for that environment.

Return schema:
{
  "mode": "plan",
  "intent": "agent_plan",
  "content": "1-3 sentence summary",
  "artifacts": {
    "environment": "browser",
    "capabilityReason": "User task involves URL and form interaction; using browser sandbox.",
    "suggestVmPermission": false,
    "steps": [
      {
        "step": 1,
        "description": "Navigate to target website",
        "tool": "browser",
        "action": "navigate",
        "params": { "url": "https://www.booking.com" },
        "expected": "Page loads successfully",
        "errorStrategy": "Retry up to 3 times"
      }
    ]
  }
}

Booking/travel: first step MUST be browser "navigate" to the correct site (e.g. https://www.booking.com or https://www.booking.com/flights), not Google.

For browser steps:
- High-consequence clicks (purchase, delete account): set "params": { "selector": "...", "highConsequence": true } or "confirmLabel": "Confirm purchase".
- Sensitive input (passwords, payment): set "params": { "selector": "...", "text": "(user will enter)", "sensitive": true, "fieldLabel": "Password" }.`,
          stream: false,
        }),
      });

      if (!response.ok) {
        throw new Error(`Planning failed: ${response.statusText}`);
      }

      const result = await response.json().catch(() => ({}));
      const contract = result?.contract && typeof result.contract === 'object' ? result.contract : null;
      const artifacts = contract?.artifacts && typeof contract.artifacts === 'object' ? contract.artifacts : {};
      let planSteps = Array.isArray(artifacts.steps) ? artifacts.steps : [];

      // Orchestrator: store selected environment and capability reason
      const env = artifacts.environment || 'browser';
      const reason = artifacts.capabilityReason || (env === 'browser' ? 'Web-only task: using browser sandbox (clicking, forms, SaaS).' : `Running in ${env} sandbox.`);
      setOrchestrator({
        environment: env,
        capabilityReason: reason,
        suggestVmPermission: Boolean(artifacts.suggestVmPermission),
      });
      if (env !== 'browser') addThought(`env: ${env} | ${reason}`);
      if (artifacts.suggestVmPermission) addLog('info', 'vm_permission_required');

      // Fallback: try to parse steps from reply content if present
      if (!planSteps.length) {
        try {
          const raw = String(result?.reply || '').trim();
          const fenced = raw.match(/```json\s*([\s\S]*?)\s*```/i);
          const bracketed = raw.match(/\[[\s\S]*\]/);
          const candidate = (fenced?.[1] || bracketed?.[0] || '').trim();
          if (candidate) {
            const parsed = JSON.parse(candidate);
            if (Array.isArray(parsed)) planSteps = parsed;
          }
        } catch (e) {
          console.warn('Failed to parse plan steps; using fallback', e);
        }
      }

      if (!planSteps.length) {
        planSteps = [
          {
            step: 1,
            description: String(result?.reply || 'Execute task').slice(0, 200) || 'Execute task',
            tool: 'general',
            action: 'execute',
            params: {},
          },
        ];
      }

      setPlan(planSteps);
      addLog('success', `Plan created with ${planSteps.length} steps`);
      addThought(`Created execution plan:\n${planSteps.map(s => `${s.step}. ${s.description}`).join('\n')}`);

      return planSteps;
    } catch (error) {
      addLog('error', `Planning error: ${error.message}`);
      throw error;
    }
  };

  // Execute a single step
  const executeStep = async (step) => {
    try {
      addLog('action', `Executing step ${step.step}: ${step.description}`);
      addThought(`Starting step ${step.step} using ${step.tool} tool...`);

      let result = null;

      switch (step.tool) {
        case 'browser':
          result = await executeBrowserAction(step);
          break;
        case 'file':
          result = await executeFileAction(step);
          break;
        case 'api':
          result = await executeApiAction(step);
          break;
        case 'terminal':
          result = await executeTerminalAction(step);
          break;
        case 'general':
        default:
          result = await executeGeneralAction(step);
          break;
      }

      addLog('success', `Step ${step.step} completed`, result);
      return result;
    } catch (error) {
      addLog('error', `Step ${step.step} failed: ${error.message}`);
      throw error;
    }
  };

  // Read operator capability from localStorage (same keys as SandboxBrowser)
  const isConfirmationRequired = () => {
    try {
      return localStorage.getItem(OPERATOR_CONFIRM_KEY) !== 'false';
    } catch (_) {
      return true;
    }
  };
  const isTakeoverEnabled = () => {
    try {
      return localStorage.getItem(OPERATOR_TAKEOVER_KEY) !== 'false';
    } catch (_) {
      return true;
    }
  };

  // Execute browser action
  const executeBrowserAction = async (step) => {
    if (!browserEnabled) {
      throw new Error('Browser automation is disabled');
    }

    addThought(`Performing browser action: ${step.action}`);

    const { action, params = {} } = step;
    const apiUrl = process.env.REACT_APP_API_URL || '';

    switch (action) {
      case 'navigate':
        setBrowserState(prev => ({ ...prev, url: params.url }));
        addLog('info', `Navigating to ${params.url}`);
        if (onBrowserAction && params.url) {
          onBrowserAction({ type: 'navigate', url: params.url });
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        return { success: true, url: params.url };

      case 'click': {
        const needsConfirmation = (params.highConsequence || params.confirmLabel) && isConfirmationRequired();
        if (needsConfirmation && typeof onConfirmRequired === 'function') {
          const label = params.confirmLabel || step.description || 'Perform this action';
          addLog('info', `High-consequence action: awaiting user confirmation for "${label}"`);
          const allowed = await onConfirmRequired({ action: 'click', ...params }, label);
          if (!allowed) {
            addLog('info', 'User declined. Skipping step.');
            return { success: false, skipped: true, reason: 'user_declined' };
          }
        }
        addLog('info', `Clicking element: ${params.selector}`);
        setBrowserState(prev => ({
          ...prev,
          actions: [...prev.actions, { type: 'click', selector: params.selector }],
        }));
        if (onBrowserAction && params.selector) {
          onBrowserAction({ type: 'click', selector: params.selector, coordinates: params.coordinates });
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        return { success: true, action: 'click', selector: params.selector };
      }

      case 'type': {
        if (params.sensitive && isTakeoverEnabled() && typeof onTakeoverRequired === 'function') {
          const fieldLabel = params.fieldLabel || 'sensitive field (e.g. password or payment)';
          addLog('info', `Takeover mode: pausing for you to enter value for "${fieldLabel}"`);
          const userValue = await onTakeoverRequired(fieldLabel, params.selector);
          addLog('success', 'You entered the value. Agent continues (value not logged).');
          setBrowserState(prev => ({
            ...prev,
            actions: [...prev.actions, { type: 'type', selector: params.selector, takeover: true }],
          }));
          return { success: true, action: 'type', takeover: true };
        }
        addLog('info', `Typing into ${params.selector}: ${params.text}`);
        setBrowserState(prev => ({
          ...prev,
          actions: [...prev.actions, { type: 'type', selector: params.selector, text: params.text }],
        }));
        if (onBrowserAction && params.selector) {
          onBrowserAction({ type: 'type', selector: params.selector, text: params.text });
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        return { success: true, action: 'type' };
      }

      case 'extract':
        addLog('info', `Extracting data from ${params.selector}`);
        return { success: true, action: 'extract', data: 'extracted-data' };

      case 'screenshot':
        addLog('info', 'Taking screenshot');
        if (onBrowserAction) {
          onBrowserAction({ type: 'screenshot' });
        }
        return { success: true, action: 'screenshot', image: 'screenshot-data' };

      default:
        throw new Error(`Unknown browser action: ${action}`);
    }
  };

  // Execute file operation
  const executeFileAction = async (step) => {
    const { action, params } = step;
    const apiUrl = process.env.REACT_APP_API_URL || '';

    addThought(`Performing file operation: ${action}`);

    try {
      const response = await fetch(`${apiUrl}/api/claude-code/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`File ${action} failed: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw new Error(`File operation failed: ${error.message}`);
    }
  };

  // Execute API call
  const executeApiAction = async (step) => {
    const { params } = step;
    addThought(`Making API call to ${params.url}`);

    try {
      const response = await fetch(params.url, {
        method: params.method || 'GET',
        headers: params.headers || {},
        body: params.body ? JSON.stringify(params.body) : undefined,
      });

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      throw new Error(`API call failed: ${error.message}`);
    }
  };

  // Execute terminal command
  const executeTerminalAction = async (step) => {
    const { params } = step;
    const apiUrl = process.env.REACT_APP_API_URL || '';

    addThought(`Executing terminal command: ${params.command}`);

    try {
      const response = await fetch(`${apiUrl}/api/claude-code/bash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command: params.command,
          description: step.description,
        }),
      });

      if (!response.ok) {
        throw new Error(`Terminal command failed: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw new Error(`Terminal execution failed: ${error.message}`);
    }
  };

  // Execute general AI-powered action
  const executeGeneralAction = async (step) => {
    addThought('Using AI to execute general task...');

    const apiBase = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    const endpoint = apiBase ? `${apiBase}/chat` : '/chat';
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: 'autonomous-agent',
        message: `Execute this task: ${step.description}\n\nProvide the result and any relevant information.`,
        stream: false,
      }),
    });

    if (!response.ok) {
      throw new Error(`General action failed: ${response.statusText}`);
    }

    const result = await response.json().catch(() => ({}));
    return { success: true, result: result.reply || '' };
  };

  // Review and verify step completion
  const reviewStep = async (step, result) => {
    addThought(`Reviewing step ${step.step} completion...`);

    // Use AI to verify if the step was successful
    const apiBase = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    const endpoint = apiBase ? `${apiBase}/chat` : '/chat';
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: 'autonomous-agent',
        message: `Review this step execution:

Step: ${step.description}
Expected: ${step.expected}
Result: ${JSON.stringify(result)}

Was this step successful? Answer with JSON: { "success": true/false, "reason": "explanation" }`,
        stream: false,
      }),
    });

    const review = await response.json().catch(() => ({}));

    try {
      const jsonMatch = review.reply?.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return parsed.success;
      }
    } catch (e) {
      console.warn('Failed to parse review', e);
    }

    // Default to success if we can't parse
    return true;
  };

  // Main execution loop
  const executeTask = async () => {
    if (!task) return;

    try {
      setState('executing');
      addLog('info', `Starting autonomous execution of: ${task}`);

      // Create plan
      const steps = await createPlan(task);

      // Execute each step
      for (let i = 0; i < steps.length; i++) {
        setCurrentStep(i);
        const step = steps[i];

        let success = false;
        let attempts = 0;

        // Retry loop
        while (!success && attempts < MAX_RETRIES) {
          try {
            const result = await executeStep(step);

            // User declined a high-consequence action: skip retries and continue to next step
            if (result?.skipped) {
              addThought(`Step ${step.step} was skipped by user. Continuing.`);
              success = true;
              break;
            }

            // Review step
            setState('reviewing');
            success = await reviewStep(step, result);

            if (!success) {
              attempts++;
              addThought(`Step ${step.step} needs retry (attempt ${attempts}/${MAX_RETRIES})`);
              if (attempts < MAX_RETRIES) {
                await new Promise(resolve => setTimeout(resolve, 2000)); // Wait before retry
              }
            }
          } catch (error) {
            attempts++;
            addLog('error', `Step ${step.step} error (attempt ${attempts}/${MAX_RETRIES}): ${error.message}`);

            if (attempts < MAX_RETRIES) {
              addThought(`Retrying step ${step.step} after error...`);
              await new Promise(resolve => setTimeout(resolve, 2000));
            } else {
              throw new Error(`Step ${step.step} failed after ${MAX_RETRIES} attempts: ${error.message}`);
            }
          }
        }

        if (!success) {
          throw new Error(`Step ${step.step} failed verification`);
        }

        setState('executing');
      }

      // Task complete â€“ record for future planning heuristics
      setState('complete');
      addLog('success', 'Task completed successfully!');
      addThought('All steps executed successfully. Task complete.');

      const experience = {
        task: task,
        success: true,
        stepsCount: steps.length,
        notes: 'completed',
      };
      pastExperienceRef.current = [...pastExperienceRef.current, experience].slice(-MAX_PAST_EXPERIENCE);

      if (onComplete) {
        onComplete({ success: true, logs, thoughts });
      }
    } catch (error) {
      setState('error');
      addLog('error', `Task failed: ${error.message}`);
      addThought(`Task execution failed: ${error.message}`);

      const experience = {
        task: task,
        success: false,
        stepsCount: currentStep + 1,
        notes: error.message?.slice(0, 60) || 'failed',
      };
      pastExperienceRef.current = [...pastExperienceRef.current, experience].slice(-MAX_PAST_EXPERIENCE);

      if (onComplete) {
        onComplete({ success: false, error: error.message, logs, thoughts });
      }
    }
  };

  // Start execution when task is provided and agent is active
  useEffect(() => {
    if (isActive && task && state === 'idle') {
      executeTask();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isActive, task]);

  // Render
  return (
    <div className="autonomous-agent">
      <div className="agent-header">
        <h3>ðŸ¤– Autonomous Agent</h3>
        <div className={`agent-state state-${state}`}>
          {state.toUpperCase()}
        </div>
      </div>
      <p className="agent-capability"><code>llm</code> + <code>remote_vm</code> + <code>browser</code> + <code>file_system</code> | <code>run_code</code> | <code>install_packages</code> | <code>create_files</code></p>

      <div className="agent-content">
        {/* Task Info */}
        <div className="task-info">
          <strong>Task:</strong> {task}
        </div>

        {/* Orchestrator: environment + reason */}
        {(orchestrator.environment || orchestrator.capabilityReason) && (
          <div className="orchestrator-section">
            <h4><code>orchestrator</code></h4>
            <div className="orchestrator-env">
              {orchestrator.environment && (
                <span className={`orchestrator-badge env-${orchestrator.environment}`}>
                  {orchestrator.environment === 'browser' && 'browser'}
                  {orchestrator.environment === 'vm' && 'vm'}
                  {orchestrator.environment === 'image' && 'image'}
                  {orchestrator.environment === 'video' && 'video'}
                  {orchestrator.environment === 'hybrid' && 'hybrid'}
                  {!['browser','vm','image','video','hybrid'].includes(orchestrator.environment) && orchestrator.environment}
                </span>
              )}
              {orchestrator.capabilityReason && (
                <p className="orchestrator-reason"><code>reason</code>: {orchestrator.capabilityReason}</p>
              )}
              {orchestrator.suggestVmPermission && (
                <p className="orchestrator-vm-note"><code>vm_permission_required</code></p>
              )}
            </div>
          </div>
        )}

        {/* Progress */}
        {plan.length > 0 && (
          <div className="progress-section">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${((currentStep + 1) / plan.length) * 100}%` }}
              />
            </div>
            <div className="progress-text">
              Step {currentStep + 1} of {plan.length}
            </div>
          </div>
        )}

        {/* Thoughts (Reasoning) */}
        {thoughts.length > 0 && (
          <div className="thoughts-section">
            <h4>ðŸ’­ Agent Reasoning</h4>
            <div className="thoughts-list">
              {thoughts.slice(-5).map(thought => (
                <div key={thought.id} className="thought-item">
                  <span className="thought-time">
                    {new Date(thought.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="thought-content">{thought.content}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Execution Logs */}
        <div className="logs-section">
          <h4>ðŸ“‹ Execution Logs</h4>
          <div className="logs-list">
            {logs.slice(-10).map(log => (
              <div key={log.id} className={`log-item log-${log.type}`}>
                <span className="log-time">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                <span className="log-icon">
                  {log.type === 'success' && 'âœ“'}
                  {log.type === 'error' && 'âœ—'}
                  {log.type === 'info' && 'â„¹'}
                  {log.type === 'thought' && 'ðŸ’­'}
                  {log.type === 'action' && 'âš¡'}
                </span>
                <span className="log-message">{log.message}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Browser State (if browser is enabled) */}
        {browserEnabled && browserState.url && (
          <div className="browser-section">
            <h4>ðŸŒ Browser State</h4>
            <div className="browser-info">
              <div>URL: {browserState.url}</div>
              <div>Actions: {browserState.actions.length}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
